import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import classNames from 'classnames';
import {
  type CSSProperties,
  type FocusEvent,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useState,
} from 'react';
import ReactSelect, {
  type ClassNamesConfig,
  type ClearIndicatorProps,
  type ControlProps,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type Props as ReactSelectProps,
  type SelectInstance,
} from 'react-select';
import ReactCreatableSelect, {
  type CreatableProps,
} from 'react-select/creatable';

import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './Select.module.css';

type Size = 'sm' | 'md' | 'lg';

type SelectVariant = 'outline' | 'filled' | 'ghost';

export type SelectCssVar =
  | '--select-disabled-bg'
  | '--select-disabled-border'
  | '--select-disabled-color';

export type SelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<
  ReactSelectProps<Option, IsMulti, Group>,
  'className' | 'classNames'
> & {
  startSlot?: ReactNode;
  size?: Size;
  variant?: SelectVariant;
  className?: string;
  isInvalid?: boolean;
  style?: CSSProperties;
  cssVars?: Partial<Record<SelectCssVar, string>>;
  'data-testid'?: string;
};

export type CreatableSelectProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = SelectProps<Option, IsMulti, Group> &
  Pick<
    CreatableProps<Option, IsMulti, Group>,
    | 'allowCreateWhileLoading'
    | 'createOptionPosition'
    | 'formatCreateLabel'
    | 'getNewOptionData'
    | 'isValidNewOption'
    | 'onCreateOption'
  >;

type SelectControlProps<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ControlProps<Option, IsMulti, Group> & {
  selectProps: ControlProps<Option, IsMulti, Group>['selectProps'] &
    Pick<SelectProps<Option, IsMulti, Group>, 'startSlot'>;
};

const ClearIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ClearIndicatorProps<Option, IsMulti, Group>
) => (
  <components.ClearIndicator {...props}>
    <IconCrossSmall size={16} />
  </components.ClearIndicator>
);

const DropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>
) => (
  <components.DropdownIndicator {...props}>
    <IconChevronDownSmall size={16} />
  </components.DropdownIndicator>
);

const IndicatorSeparator = () => null;

const Control = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  selectProps,
  ...props
}: SelectControlProps<Option, IsMulti, Group>) => (
  <components.Control {...props} selectProps={selectProps}>
    {selectProps.startSlot ? (
      <span className={styles.startSlot} aria-hidden="true">
        {selectProps.startSlot}
      </span>
    ) : null}
    {children}
  </components.Control>
);

const StyledSelectInner = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  SelectComponent: typeof ReactSelect,
  {
    size = 'md',
    variant = 'outline',
    startSlot,
    className,
    isInvalid,
    style,
    cssVars,
    components: componentOverrides,
    styles: styleOverrides,
    'data-testid': dataTestId,
    menuPlacement = 'auto',
    maxMenuHeight = 205,
    onFocus,
    onBlur,
    onMenuOpen,
    onMenuClose,
    ...selectProps
  }: SelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>
) => {
  const menuPortalTarget = useContext(UIPortalContext);
  // react-select measures the control's VIEWPORT rect and then writes those
  // numbers onto the portalled menu as `left`/`top`. As `position: absolute`
  // that only lines up when the portal host is an unpositioned box at the
  // document origin — `<body>` or the app's `#ui-portal-root`. A modal
  // `<dialog>`, which the Dialog offers as the host so menus stay in the top
  // layer, is neither: it is `position: fixed`, so it becomes the containing
  // block and re-bases the menu by its own origin, and it has a scrollport, so
  // the displaced menu enlarges its scrollable overflow. Scrolling it then
  // moves the control, react-select re-measures, re-creates its
  // `setMenuPortalElement` ref callback, React re-attaches it, `autoUpdate`
  // synchronously re-measures — a layout-effect loop that never settles and
  // dies as "Maximum update depth exceeded". `fixed` keeps the numbers in the
  // space they were measured in and keeps the menu out of the dialog's
  // scrollable overflow entirely.
  const menuPositionDefault = menuPortalTarget?.closest('dialog')
    ? 'fixed'
    : 'absolute';
  const [focused, setFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reactSelectProps = {
    ...selectProps,
    startSlot,
  } as ReactSelectProps<Option, IsMulti, Group> & {
    startSlot?: ReactNode;
  };

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleMenuOpen = useCallback(() => {
    setMenuOpen(true);
    onMenuOpen?.();
  }, [onMenuOpen]);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    onMenuClose?.();
  }, [onMenuClose]);

  const selectClassNames: ClassNamesConfig<Option, IsMulti, Group> = {
    control: () => styles.control ?? '',
    valueContainer: () => styles.valueContainer ?? '',
    placeholder: () => styles.placeholder ?? '',
    singleValue: () => styles.singleValue ?? '',
    input: () => styles.input ?? '',
    indicatorsContainer: () => styles.indicatorsContainer ?? '',
    clearIndicator: () => styles.clearIndicator ?? '',
    dropdownIndicator: () => styles.dropdownIndicator ?? '',
    menu: () => styles.menu ?? '',
    menuList: () => styles.menuList ?? '',
    option: (state) =>
      classNames(
        styles.option,
        state.isFocused && styles.optionFocused,
        state.isSelected && styles.optionSelected,
        state.isDisabled && styles.optionDisabled,
        size === 'sm' && styles.sm,
        size === 'md' && styles.md,
        size === 'lg' && styles.lg
      ),
    group: () =>
      classNames(
        styles.group,
        size === 'sm' && styles.sm,
        size === 'md' && styles.md,
        size === 'lg' && styles.lg
      ),
    groupHeading: () => styles.groupHeading ?? '',
    noOptionsMessage: () => styles.message ?? '',
    loadingMessage: () => styles.message ?? '',
    multiValue: () => styles.multiValue ?? '',
    multiValueLabel: () => styles.multiValueLabel ?? '',
    multiValueRemove: () => styles.multiValueRemove ?? '',
  };

  return (
    <div
      className={classNames(
        styles.wrapper,
        styles[size],
        variant !== 'outline' && styles[variant],
        className
      )}
      style={{ ...style, ...(cssVars as CSSProperties) }}
      data-testid={dataTestId}
      data-focused={focused || undefined}
      data-disabled={selectProps.isDisabled || undefined}
      data-invalid={isInvalid || undefined}
      data-open={menuOpen || undefined}
    >
      <SelectComponent<Option, IsMulti, Group>
        ref={ref}
        unstyled
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPositionDefault}
        maxMenuHeight={maxMenuHeight}
        menuPlacement={menuPlacement}
        components={{
          Control,
          ClearIndicator,
          DropdownIndicator,
          IndicatorSeparator,
          ...componentOverrides,
        }}
        classNames={selectClassNames}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          ...styleOverrides,
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        {...reactSelectProps}
      />
    </div>
  );
};

const SelectInner = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>
) => StyledSelectInner(ReactSelect, props, ref);

const CreatableSelectInner = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: CreatableSelectProps<Option, IsMulti, Group>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>
) =>
  StyledSelectInner(
    ReactCreatableSelect as unknown as typeof ReactSelect,
    props,
    ref
  );

/* forwardRef erases generics, so the cast restores the generic call signature.
   The ref is the underlying react-select instance — react-hook-form's
   controller ref relies on its `.focus()` for validation focus management. */
export const Select = forwardRef(SelectInner) as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectProps<Option, IsMulti, Group> & {
    ref?: Ref<SelectInstance<Option, IsMulti, Group>>;
  }
) => ReactElement;

export const CreatableSelect = forwardRef(CreatableSelectInner) as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: CreatableSelectProps<Option, IsMulti, Group> & {
    ref?: Ref<SelectInstance<Option, IsMulti, Group>>;
  }
) => ReactElement;
